import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Plus, Target, Trash2 } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Barra } from "@/componentes/estrategia/Barra"
import { FormularioObjetivo } from "@/componentes/estrategia/FormularioObjetivo"
import { ListaEtapas } from "@/componentes/estrategia/ListaEtapas"
import type { Despliegue, Objetivo, Pilar } from "@/componentes/estrategia/tipos"
import { KpiEstrategia } from "@/componentes/kpi/KpiPlanificacion"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/**
 * El despliegue estratégico: de la visión a un hito con fecha.
 *
 * ⚠ **El porcentaje NO se teclea en ningún sitio: se cuenta.** El de un objetivo sale
 * de sus etapas completadas, el de un pilar de sus objetivos y el global de los
 * pilares que tienen objetivos. Es la misma decisión que en el ledger financiero, y
 * por el mismo motivo: un avance que alguien mantiene a mano acaba diciendo 80 % con
 * todo sin hacer, y nadie lo nota hasta la reunión en que se presenta.
 *
 * ⚠ **Un pilar sin objetivos no cuenta para el avance global.** Si contara como cero,
 * declarar un pilar nuevo bajaría el avance de la empresa — y entonces nadie declara
 * pilares, que es justo lo contrario de lo que esta pantalla busca.
 *
 * ⚠ **«Vencido» lo decide el servidor.** Con el reloj del navegador, dos personas en
 * husos distintos verían vencidos distintos sobre los mismos datos.
 */

export function Estrategia() {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeEditar = tienePermiso(usuario, "estrategia:editar")

  const [creandoPilar, setCreandoPilar] = useState(false)
  const [origen, setOrigen] = useState<"VISION" | "MISION">("VISION")
  const [tituloPilar, setTituloPilar] = useState("")
  const [enObjetivo, setEnObjetivo] = useState<{ pilar: Pilar; objetivo: Objetivo | null } | null>(
    null,
  )

  const despliegue = useQuery({
    queryKey: ["estrategia"],
    queryFn: () => api.get<Despliegue>("/api/v1/estrategia"),
  })

  const refrescar = () => clienteQuery.invalidateQueries({ queryKey: ["estrategia"] })

  const crearPilar = useMutation({
    mutationFn: () =>
      api.post<Pilar>("/api/v1/estrategia/pilares", { origen, titulo: tituloPilar }),
    onSuccess: () => {
      refrescar()
      setCreandoPilar(false)
      setTituloPilar("")
    },
  })

  const borrarPilar = useMutation({
    mutationFn: (id: number) => api.delete(`/api/v1/estrategia/pilares/${id}`),
    onSuccess: refrescar,
  })

  const borrarObjetivo = useMutation({
    mutationFn: (id: number) => api.delete(`/api/v1/estrategia/objetivos/${id}`),
    onSuccess: refrescar,
  })

  const pilares = despliegue.data?.pilares ?? []

  return (
    <div>
      {/* Ni título ni frase propios: los pone la cabecera de «Planificación
          estratégica». Aquí arriba sólo van las acciones, a la derecha. */}
      {puedeEditar && (
        <div className="mb-4 flex justify-end">
          <Boton variante="primario" onClick={() => setCreandoPilar(true)}>
            <Plus size={16} aria-hidden="true" />
            Nuevo pilar
          </Boton>
        </div>
      )}
      <KpiEstrategia />

      {despliegue.data && (
        <div className="mb-6 superficie p-4">
          <p className="mb-2 text-xs font-semibold uppercase" style={{ color: "var(--fg-muted)" }}>
            Avance global
          </p>
          <Barra porcentaje={despliegue.data.avance} />
          <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            Media de los pilares que tienen objetivos. Los pilares vacíos no cuentan.
          </p>
        </div>
      )}

      {despliegue.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando el despliegue…
        </p>
      )}

      {despliegue.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudo cargar el despliegue estratégico
        </p>
      )}

      {despliegue.data && pilares.length === 0 && (
        <div
          className="flex flex-col items-center gap-2 superficie py-12"
          style={{ color: "var(--fg-muted)" }}
        >
          <Target size={22} aria-hidden="true" />
          <p className="text-sm">Todavía no hay pilares declarados</p>
        </div>
      )}

      <div className="space-y-6">
        {pilares.map((pilar) => (
          <section key={pilar.id} className="superficie p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Insignia tono={pilar.origen === "VISION" ? "primario" : "info"}>
                    {pilar.origen === "VISION" ? "Visión" : "Misión"}
                  </Insignia>
                  <h2 className="text-base font-semibold">{pilar.titulo}</h2>
                </div>
                {pilar.descripcion && (
                  <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
                    {pilar.descripcion}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {puedeEditar && (
                  <>
                    <Boton
                      variante="fantasma"
                      tamano="sm"
                      onClick={() => setEnObjetivo({ pilar, objetivo: null })}
                    >
                      <Plus size={14} aria-hidden="true" />
                      Objetivo
                    </Boton>
                    <Boton
                      variante="fantasma"
                      tamano="sm"
                      onClick={() => borrarPilar.mutate(pilar.id)}
                      aria-label={`Eliminar el pilar ${pilar.titulo}`}
                      style={{ color: "var(--destructive)" }}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </Boton>
                  </>
                )}
              </div>
            </div>

            <div className="mt-3">
              <Barra porcentaje={pilar.avance} />
            </div>

            {pilar.objetivos.length === 0 ? (
              <p className="mt-4 text-sm" style={{ color: "var(--fg-muted)" }}>
                Sin objetivos: este pilar todavía no aporta al avance.
              </p>
            ) : (
              <ul className="mt-4 space-y-4">
                {pilar.objetivos.map((objetivo) => (
                  <li
                    key={objetivo.id}
                    className="rounded-[var(--radius-control)] border p-3"
                    style={{ borderColor: "var(--border)" }}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {objetivo.enunciado}
                          {objetivo.vencido && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 text-xs"
                              style={{ color: "var(--destructive)" }}
                            >
                              <AlertCircle size={12} aria-hidden="true" /> Vencido
                            </span>
                          )}
                        </p>
                        <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                          {objetivo.indicador ?? "Sin indicador"}
                          {objetivo.meta !== null &&
                            ` · meta ${objetivo.meta}${objetivo.unidad ? ` ${objetivo.unidad}` : ""}`}
                          {objetivo.responsable && ` · ${objetivo.responsable}`}
                          {objetivo.fecha_fin && ` · ${comoFecha(objetivo.fecha_fin)}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold tabular">{objetivo.avance} %</span>
                        {puedeEditar && (
                          <>
                            <Boton
                              variante="fantasma"
                              tamano="sm"
                              onClick={() => setEnObjetivo({ pilar, objetivo })}
                            >
                              Editar
                            </Boton>
                            <Boton
                              variante="fantasma"
                              tamano="sm"
                              onClick={() => borrarObjetivo.mutate(objetivo.id)}
                              aria-label={`Eliminar el objetivo ${objetivo.enunciado}`}
                              style={{ color: "var(--destructive)" }}
                            >
                              <Trash2 size={14} aria-hidden="true" />
                            </Boton>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mt-2">
                      <Barra porcentaje={objetivo.avance} />
                    </div>
                    <ListaEtapas
                      objetivo={objetivo}
                      puedeEditar={puedeEditar}
                      alCambiar={refrescar}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <Modal
        abierto={creandoPilar}
        titulo="Nuevo pilar"
        descripcion="Un pilar despliega la visión o la misión en algo que se pueda perseguir."
        onCerrar={() => setCreandoPilar(false)}
        ancho="sm"
      >
        <label htmlFor="origen-pilar" className="mb-1 block text-xs font-medium uppercase">
          Despliega la
        </label>
        <select
          id="origen-pilar"
          value={origen}
          onChange={(e) => setOrigen(e.target.value as "VISION" | "MISION")}
          className="mb-4 h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--surface)",
            color: "var(--fg)",
          }}
        >
          <option value="VISION">Visión</option>
          <option value="MISION">Misión</option>
        </select>

        <label htmlFor="titulo-pilar" className="mb-1 block text-xs font-medium uppercase">
          Título
        </label>
        <input
          id="titulo-pilar"
          value={tituloPilar}
          onChange={(e) => setTituloPilar(e.target.value)}
          maxLength={200}
          className="mb-4 h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--surface)",
            color: "var(--fg)",
          }}
        />
        {crearPilar.isError && (
          <p role="alert" className="mb-3 text-sm" style={{ color: "var(--destructive)" }}>
            {crearPilar.error instanceof ErrorApi ? crearPilar.error.message : "No se pudo crear"}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Boton variante="fantasma" onClick={() => setCreandoPilar(false)}>
            Cancelar
          </Boton>
          <Boton
            variante="primario"
            onClick={() => crearPilar.mutate()}
            disabled={!tituloPilar.trim() || crearPilar.isPending}
          >
            Crear
          </Boton>
        </div>
      </Modal>

      {enObjetivo && (
        <FormularioObjetivo
          pilarId={enObjetivo.pilar.id}
          objetivo={enObjetivo.objetivo}
          onCerrar={() => setEnObjetivo(null)}
          onGuardado={refrescar}
        />
      )}
    </div>
  )
}
